import React, { useMemo } from 'react';
import { KpiCard } from '../components/ui/KpiCard';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { AlertCircle, Info, Sparkles, TrendingUp } from 'lucide-react';
import { useDataStore } from '../context/DataStoreContext';
import { CURRENT_USER } from '../constants/branding';
export function Dashboard() {
  const {
    leads,
    qaRecords
  } = useDataStore();
  // Derived KPIs
  const leadsVerificados = leads.filter(l => l.VERIFICADO).length;
  const cicloActivo = leads.filter(l => !['Nuevo', 'Rechazado', 'Entregado'].includes(l.STATE)).length;
  const microAcuerdos = leads.filter(l => l.STATE === 'Micro-acuerdo').length;
  const leadsCualificados = leads.filter(l => l.STATE === 'LeadCualificado').length;
  // Derived Sources
  const barData = useMemo(() => {
    const sources: Record<string, number> = {};
    leads.forEach(l => {
      sources[l.LEAD_SOURCE] = (sources[l.LEAD_SOURCE] || 0) + 1;
    });
    return Object.entries(sources).map(([name, value]) => ({
      name,
      value
    })).sort((a, b) => b.value - a.value);
  }, [leads]);
  // Derived Territories
  const territories = useMemo(() => {
    const terrs: Record<string, {
      leads: number;
      cualificados: number;
    }> = {};
    leads.forEach(l => {
      if (!l.TAG_GEO) return;
      if (!terrs[l.TAG_GEO]) terrs[l.TAG_GEO] = {
        leads: 0,
        cualificados: 0
      };
      terrs[l.TAG_GEO].leads += 1;
      if (l.STATE === 'LeadCualificado') terrs[l.TAG_GEO].cualificados += 1;
    });
    return Object.entries(terrs).map(([name, data]) => ({
      name,
      leads: data.leads,
      conv: data.leads > 0 ? `${(data.cualificados / data.leads * 100).toFixed(1)}%` : '0%'
    })).sort((a, b) => b.leads - a.leads).slice(0, 5);
  }, [leads]);
  // Derived Line Data (Last 30 days)
  const lineData = useMemo(() => {
    const data = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      const dayLeads = leads.filter(l => l.CREATED_AT.startsWith(dateStr));
      data.push({
        day: d.getDate(),
        verificados: dayLeads.filter(l => l.VERIFICADO).length,
        cualificados: dayLeads.filter(l => l.STATE === 'LeadCualificado').length
      });
    }
    return data;
  }, [leads]);
  const avgQaScore = useMemo(() => {
    if (qaRecords.length === 0) return 0;
    const sum = qaRecords.reduce((acc, r) => acc + r.score, 0);
    return (sum / qaRecords.length).toFixed(1);
  }, [qaRecords]);
  const firstName = CURRENT_USER?.name?.split(' ')[0] ?? 'Ivana';
  const today = new Date();
  const hour = today.getHours();
  const greeting = hour < 12 ? 'Buenos días' : hour < 20 ? 'Buenas tardes' : 'Buenas noches';
  return <div className="p-4 md:p-8 max-w-[1100px] mx-auto space-y-8 pb-24 md:pb-8" data-id="element-911">
      {/* Hero welcome banner — large, prominent */}
      <section className="relative overflow-hidden rounded-[20px] border border-white/[0.06] bg-gradient-to-br from-[#1A1A1A] via-[#141414] to-[#0B0B0B]" data-id="element-912">
        {/* Decorative gradient orbs */}
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-growthly-green/[0.08] blur-3xl pointer-events-none" data-id="element-913" />
        <div className="absolute -bottom-20 -left-10 w-64 h-64 rounded-full bg-white/[0.04] blur-3xl pointer-events-none" data-id="element-914" />

        <div className="relative px-6 md:px-12 py-10 md:py-14" data-id="element-915">
          <div className="flex items-center gap-2 mb-5" data-id="element-916">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/[0.06] border border-white/[0.08] text-[11px] font-medium text-growthly-text-2 tracking-wide" data-id="element-917">
              <Sparkles size={12} className="text-growthly-amber" data-id="element-918" />
              {greeting}
            </div>
            <span className="text-[12px] text-growthly-muted" data-id="element-919">
              {today.toLocaleDateString('es-ES', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
            </span>
          </div>

          <h1 className="text-[44px] md:text-[64px] lg:text-[72px] font-bold text-white leading-[1.02] tracking-tight" data-id="element-920">
            Bienvenida{' '}
            <span className="bg-gradient-to-r from-white via-white to-growthly-green/80 bg-clip-text text-transparent" data-id="element-921">
              {firstName}
            </span>
          </h1>
          <p className="text-[16px] md:text-[18px] text-growthly-text-2 mt-4 max-w-2xl leading-relaxed" data-id="element-922">
            {CURRENT_USER?.name ?? 'Ivana Jovanoska Risteska'} ·{' '}
            <span className="text-growthly-muted" data-id="element-923">
              {CURRENT_USER?.role ?? 'Marketing Manager'} en Growthly
              Corporation Systems
            </span>
          </p>
          <p className="text-[14px] text-growthly-muted mt-3 max-w-2xl" data-id="element-924">
            Aquí tienes tu vista operativa en tiempo real. Todos los datos
            provienen de tu actividad.
          </p>

          {/* Quick stats inline */}
          <div className="flex flex-wrap items-center gap-6 mt-8 pt-6 border-t border-white/[0.06]" data-id="element-925">
            <div data-id="element-926">
              <p className="text-[11px] uppercase tracking-wider text-growthly-muted mb-1" data-id="element-927">
                Total leads
              </p>
              <p className="text-[22px] font-bold text-white" data-id="element-928">{leads.length}</p>
            </div>
            <div className="h-10 w-px bg-white/[0.06]" data-id="element-929" />
            <div data-id="element-930">
              <p className="text-[11px] uppercase tracking-wider text-growthly-muted mb-1" data-id="element-931">
                En ciclo activo
              </p>
              <p className="text-[22px] font-bold text-white" data-id="element-932">{cicloActivo}</p>
            </div>
            <div className="h-10 w-px bg-white/[0.06]" data-id="element-933" />
            <div data-id="element-934">
              <p className="text-[11px] uppercase tracking-wider text-growthly-muted mb-1" data-id="element-935">
                Cualificados
              </p>
              <p className="text-[22px] font-bold text-growthly-green" data-id="element-936">
                {leadsCualificados}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between" data-id="element-937">
        <div className="flex items-center gap-2.5" data-id="element-938">
          <TrendingUp size={18} className="text-growthly-muted" data-id="element-939" />
          <h2 className="text-[22px] font-bold text-growthly-text" data-id="element-940">
            Dashboard Operativo
          </h2>
        </div>
        <div className="text-[13px] text-growthly-muted" data-id="element-941">
          {new Date().toLocaleDateString('es-ES', {
          month: 'long',
          year: 'numeric'
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-942">
        <KpiCard title="Leads Verificados" value={leadsVerificados} target={800} progress={leadsVerificados / 800 * 100} delta={0} deltaType="neutral" data-id="element-943" />
        <KpiCard title="Ciclo Activo" value={cicloActivo} delta={0} deltaType="neutral" data-id="element-944" />
        <KpiCard title="Micro-acuerdos" value={microAcuerdos} delta={0} deltaType="neutral" data-id="element-945" />
        <KpiCard title="Leads Cualificados" value={leadsCualificados} target={35} progress={leadsCualificados / 35 * 100} delta={0} deltaType="neutral" data-id="element-946" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-947">
        <div className="lg:col-span-2 space-y-6" data-id="element-948">
          <div className="card p-5" data-id="element-949">
            <h3 className="text-[14px] font-medium text-growthly-muted mb-4" data-id="element-950">
              Evolución 30 días
            </h3>
            {leads.length === 0 ? <div className="h-[250px] w-full flex items-center justify-center text-growthly-muted text-[13px] border border-dashed border-white/10 rounded-lg" data-id="element-951">
                Añade leads para ver la evolución
              </div> : <div className="h-[250px] w-full" data-id="element-952">
                <ResponsiveContainer width="100%" height="100%" data-id="element-953">
                  <LineChart data={lineData} margin={{
                top: 5,
                right: 20,
                bottom: 5,
                left: -20
              }} data-id="element-954">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.06)" data-id="element-955" />
                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#BDBDBD'
                }} data-id="element-956" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#BDBDBD'
                }} data-id="element-957" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#1F1F1F',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#FFFFFF'
                }} itemStyle={{
                  color: '#FFFFFF'
                }} data-id="element-958" />
                    <Legend iconType="circle" wrapperStyle={{
                  fontSize: '12px',
                  color: '#E6E6E6'
                }} data-id="element-959" />
                    <Line type="monotone" dataKey="verificados" name="Verificados" stroke="#FFFFFF" strokeWidth={2} dot={false} data-id="element-960" />
                    <Line type="monotone" dataKey="cualificados" name="Cualificados" stroke="#27AE60" strokeWidth={2} dot={false} data-id="element-961" />
                  </LineChart>
                </ResponsiveContainer>
              </div>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-962">
            <div className="card p-5" data-id="element-963">
              <h3 className="text-[14px] font-medium text-growthly-muted mb-4" data-id="element-964">
                Canales de Origen
              </h3>
              {barData.length === 0 ? <div className="h-[200px] w-full flex items-center justify-center text-growthly-muted text-[13px] border border-dashed border-white/10 rounded-lg" data-id="element-965">
                  Sin datos
                </div> : <div className="h-[200px] w-full" data-id="element-966">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-967">
                    <BarChart data={barData} layout="vertical" margin={{
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 20
                }} data-id="element-968">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="rgba(255,255,255,0.06)" data-id="element-969" />
                      <XAxis type="number" hide data-id="element-970" />
                      <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{
                    fontSize: 12,
                    fill: '#BDBDBD'
                  }} data-id="element-971" />
                      <Tooltip cursor={{
                    fill: 'rgba(255,255,255,0.05)'
                  }} contentStyle={{
                    backgroundColor: '#1F1F1F',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }} data-id="element-972" />
                      <Bar dataKey="value" fill="#FFFFFF" radius={[0, 4, 4, 0]} barSize={20} data-id="element-973" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>}
            </div>

            <div className="card p-5" data-id="element-974">
              <h3 className="text-[14px] font-medium text-growthly-muted mb-4" data-id="element-975">
                Top Territorios
              </h3>
              {territories.length === 0 ? <div className="h-[200px] w-full flex items-center justify-center text-growthly-muted text-[13px] border border-dashed border-white/10 rounded-lg" data-id="element-976">
                  Sin datos
                </div> : <div className="overflow-x-auto" data-id="element-977">
                  <table className="w-full text-[13px] text-left" data-id="element-978">
                    <thead className="text-[12px] text-growthly-muted border-b border-white/5" data-id="element-979">
                      <tr data-id="element-980">
                        <th className="pb-2 font-medium" data-id="element-981">Territorio</th>
                        <th className="pb-2 font-medium text-right" data-id="element-982">Leads</th>
                        <th className="pb-2 font-medium text-right" data-id="element-983">Conv.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-984">
                      {territories.map((t, i) => <tr key={i} className="hover:bg-white/[0.02] transition-colors" data-id="element-985">
                          <td className="py-2.5 font-medium text-growthly-text" data-id="element-986">
                            {t.name}
                          </td>
                          <td className="py-2.5 text-right text-growthly-muted" data-id="element-987">
                            {t.leads}
                          </td>
                          <td className="py-2.5 text-right font-medium text-growthly-green" data-id="element-988">
                            {t.conv}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </div>
        </div>

        <div className="space-y-4" data-id="element-989">
          <h3 className="text-[14px] font-medium text-growthly-muted" data-id="element-990">
            Alertas Activas
          </h3>

          {leadsVerificados < 200 && leads.length > 0 && <div className="bg-growthly-red/5 border border-growthly-red/30 rounded-lg p-4 shadow-sm flex items-start space-x-3" data-id="element-991">
              <AlertCircle className="w-5 h-5 text-growthly-red flex-shrink-0 mt-0.5" data-id="element-992" />
              <div data-id="element-993">
                <h4 className="text-[14px] font-semibold text-growthly-red" data-id="element-994">
                  Plan de Choque Activo
                </h4>
                <p className="text-[12px] text-growthly-red/80 mt-1" data-id="element-995">
                  Acumulado &le; 30% del objetivo. Incremento de intentos +20%
                  requerido.
                </p>
              </div>
            </div>}

          {qaRecords.length > 0 && Number(avgQaScore) < 15 && <div className="bg-growthly-surface border border-white/10 rounded-lg p-4 shadow-sm flex items-start space-x-3" data-id="element-996">
              <Info className="w-5 h-5 text-growthly-muted flex-shrink-0 mt-0.5" data-id="element-997" />
              <div data-id="element-998">
                <h4 className="text-[14px] font-semibold text-growthly-text" data-id="element-999">
                  QA Score Bajo
                </h4>
                <p className="text-[12px] text-growthly-muted mt-1" data-id="element-1000">
                  Tu media es {avgQaScore}/20. Revisa tus últimas evaluaciones.
                </p>
              </div>
            </div>}

          <div className="bg-growthly-surface border border-white/10 rounded-lg p-4 shadow-sm flex items-start space-x-3" data-id="element-1001">
            <div className="w-2 h-2 rounded-full bg-growthly-green mt-1.5 flex-shrink-0" data-id="element-1002"></div>
            <div data-id="element-1003">
              <h4 className="text-[14px] font-medium text-growthly-text" data-id="element-1004">
                SLA Enriquecimiento OK
              </h4>
              <p className="text-[12px] text-growthly-muted mt-1" data-id="element-1005">
                {leads.filter(l => !l.ENRICHMENT_READY).length} leads
                pendientes de enriquecimiento inicial.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}